import { useState, useRef, useEffect, useCallback } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/lib/components/Popover";
import { Button } from "@/lib/components/Button/Button";
import { IconCheck, IconClockHour5, IconListCheck } from "@tabler/icons-react";
import { Input } from "./Input";
import { cn, isValidNumber } from "../utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "./Tooltip";

type CommitAction = (value: number) => void;

type Props = {
  value: number;
  suffix?: string;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  inputDecimalPlaces?: number;
  className?: string;
  variant?: "default" | "button";
  onCommit: CommitAction;
  onCommitMultiple?: CommitAction;
};

export default function SliderInputField({
  value,
  suffix,
  min,
  max,
  decimalPlaces = 1,
  inputDecimalPlaces = decimalPlaces,
  className,
  variant = "default",
  onCommit,
  onCommitMultiple,
}: Props) {
  const [open, setOpen] = useState(false);
  const label = `${value.toFixed(decimalPlaces)}${suffix ?? ""}`.trim();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === "default" ? (
          <p
            className={cn(
              "ml-2 max-w-8 min-w-8 cursor-pointer text-right text-sm font-medium text-text-secondary transition-colors hover:text-text-primary",
              className,
            )}
          >
            {label}
          </p>
        ) : (
          <button
            className={cn(
              "flex h-5.5 cursor-pointer items-center gap-1 rounded-md border border-tertiary-400/5 bg-tertiary-100 px-1 text-sm font-semibold text-tertiary-400 transition-colors hover:bg-tertiary-150",
              className,
            )}
            contentEditable={false}
          >
            <IconClockHour5 size={14} stroke={2.5} />
            <p className="ml-0 max-w-none min-w-0 py-0.5 pr-1 text-xs font-semibold">
              {label}
            </p>
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent className="w-auto p-2.5" align="center" side="top">
        <InputPopoverContent
          initialValue={value}
          min={min}
          max={max}
          decimalPlaces={inputDecimalPlaces}
          onCommit={onCommit}
          onCommitMultiple={onCommitMultiple}
          setOpen={setOpen}
        />
      </PopoverContent>
    </Popover>
  );
}

type InputPopoverContentProps = {
  initialValue: number;
  min?: number;
  max?: number;
  decimalPlaces?: number;
  onCommit: CommitAction;
  onCommitMultiple?: CommitAction;
  setOpen: (open: boolean) => void;
};

function InputPopoverContent({
  initialValue,
  min,
  max,
  decimalPlaces,
  onCommit,
  onCommitMultiple,
  setOpen,
}: InputPopoverContentProps) {
  const [internalValue, setInternalValue] = useState(initialValue.toString());
  const inputRef = useRef<HTMLInputElement>(null);
  const shouldCommitOnClose = useRef(true);
  // const internalValueRef = useRef(internalValue);

  const commitInternalValue = useCallback(
    (actionType: "COMMIT" | "COMMIT_MULTIPLE") => {
      if (!isValidNumber(internalValue)) {
        return;
      }

      let numValue = parseFloat(internalValue);

      if (min !== undefined && numValue < min) {
        numValue = min;
      }
      if (max !== undefined && numValue > max) {
        numValue = max;
      }

      numValue = Number(numValue.toFixed(decimalPlaces));

      if (actionType === "COMMIT") {
        onCommit(numValue);
      } else {
        onCommitMultiple?.(numValue);
      }
    },
    [internalValue, min, max, decimalPlaces, onCommit, onCommitMultiple],
  );

  const commitInternalValueOnButtonClick = useCallback(
    (actionType: "COMMIT" | "COMMIT_MULTIPLE") => {
      shouldCommitOnClose.current = false;
      commitInternalValue(actionType);
      setOpen(false);
    },
    [commitInternalValue, setOpen],
  );

  const commitInternalValueRef = useRef(commitInternalValue);

  // useEffect(() => {
  //   setInternalValue(initialValue.toString());
  // }, [initialValue]);

  // Storing the values in refs to avoid triggering handleCommit before the component unmounts
  useEffect(() => {
    commitInternalValueRef.current = commitInternalValue;
  }, [commitInternalValue]);

  useEffect(() => {
    return () => {
      if (shouldCommitOnClose.current) {
        commitInternalValueRef.current("COMMIT");
      }
    };
  }, []);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [inputRef]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      commitInternalValueOnButtonClick("COMMIT");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue === "" || newValue === "-") {
      setInternalValue(newValue);
      return;
    }

    const sanitized = newValue.replace(/,/g, ".");

    if (/^-?\d*\.?\d*$/.test(sanitized)) {
      setInternalValue(sanitized);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        ref={inputRef}
        type="text"
        containerSize="xs"
        className="w-28"
        value={internalValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
      />

      <Button
        size="iconXs"
        variant="default"
        onClick={() => commitInternalValueOnButtonClick("COMMIT")}
        type="button"
      >
        <IconCheck />
      </Button>
      {onCommitMultiple && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="iconXs"
              variant="outline"
              onClick={() =>
                commitInternalValueOnButtonClick("COMMIT_MULTIPLE")
              }
              type="button"
            >
              <IconListCheck />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Apply to all clips</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
